import { APIStatus } from "@/api/APIStatus.type";
import { Button } from "@/design-system/Button/Button";
import { IMPORT_CTA_LABEL } from "@/features/MyClub/EmptyClub/emptyClubCopy";
import { useStartClubImport } from "@/features/MyClub/EmptyClub/useStartClubImport";
import {
  hasImportedBeforeSelector,
  userIsLoggedInSelector,
} from "@/redux/user/userSlice";
import { useTranslation } from "@repo/shared/i18n";
import { useSelector } from "react-redux";

type Props = {
  /**
   * The surface this empty state is rendered on ("Players", "Evolutions", ...).
   * Reused across several pages, and its CTA starts a signup flow that loses
   * its origin without it — see clubImportPrompt.noteClubImportIntent.
   */
  section: string;
  status?: APIStatus;
  /** Overrides the heading, e.g. "No planned evolutions yet". */
  title?: string;
  /** Overrides the line under the heading. */
  subtitle?: string;
  /** Extra side effect (e.g. Mixpanel tracking) fired on the logged-out CTA. */
  onCreateAccountClick?: () => void;
  /**
   * Replaces the import CTA. Planned evolutions are still made by hand, so that
   * list points at the builder rather than at an import that can't fill it.
   */
  cta?: { label: string; onClick: () => void };
};

/*
  An evolution collection can only come from an import — evolutions used to be
  typed in by hand, and that is gone; saving by hand now only plans one. So both
  flavours of empty ask for the same thing the club states ask for, in the same
  shape: title, subtitle, one import CTA.
*/
const FIRST_IMPORT = {
  title: "evolutions.emptyState.firstImportTitle",
  subtitle: "evolutions.emptyState.firstImportSubtitle",
} as const;

const REIMPORT = {
  title: "evolutions.emptyState.reimportTitle",
  subtitle: "evolutions.emptyState.reimportSubtitle",
} as const;

/**
 * Empty state shown when the "My Evolutions" filter is active but no evolved
 * players are returned. Reused by the Players page, the Evolution Players
 * section, and MyEvolvedPlayersList.
 */
export const MyEvolutionsEmptyState = ({
  section,
  status,
  title,
  subtitle,
  onCreateAccountClick,
  cta,
}: Props) => {
  const { t } = useTranslation("players");
  const { t: tMyClub } = useTranslation("myClub");
  const isLoggedIn = useSelector(userIsLoggedInSelector);
  const hasImportedBefore = useSelector(hasImportedBeforeSelector);
  const startClubImport = useStartClubImport({
    section,
    subsection: "My Evolutions",
  });

  if (isLoggedIn && status === "rejected") {
    return (
      <EmptyState
        title={t("evolutions.emptyState.loadFailedTitle")}
        subtitle={t("evolutions.emptyState.loadFailedSubtitle")}
      />
    );
  }

  const copy = hasImportedBefore ? REIMPORT : FIRST_IMPORT;

  return (
    <EmptyState
      title={title ?? t(copy.title)}
      subtitle={subtitle ?? t(copy.subtitle)}
      cta={
        cta ?? {
          label: tMyClub(IMPORT_CTA_LABEL),
          onClick: () => {
            // The tutorial asks a logged-out visitor to sign in, so their
            // click still belongs to the signup funnel. The import CTA event
            // itself is fired by useStartClubImport.
            if (!isLoggedIn) onCreateAccountClick?.();
            startClubImport();
          },
        }
      }
    />
  );
};

const EmptyState = ({
  title,
  subtitle,
  cta,
}: {
  title: string;
  subtitle?: string;
  cta?: { label: string; onClick: () => void };
}) => (
  <div className="flex w-full flex-col items-center gap-y-4 py-12 text-center">
    <div className="flex flex-col items-center gap-y-1">
      <h3 className="text-h3 text-foreground">{title}</h3>
      {subtitle && (
        <p className="text-body text-foreground-muted">{subtitle}</p>
      )}
    </div>
    {cta && (
      <Button variant="primary" size="large" onClick={cta.onClick}>
        {cta.label}
      </Button>
    )}
  </div>
);
